import { Download } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  Banner,
  Button,
  Checkbox,
  Spinner
} from '@thedealersconcierge/components';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  FormCollectionStatus,
  FormSubmissionStatus
} from '~/__generated__/backend/zeus';
import Header from '~/components/Header';
import SectionElementRow from '~/components/SectionElementRow';
import ChevronRightIcon from '~/components/icons/ChevronRightIcon';
import DocumentIcon from '~/components/icons/DocumentIcon';
import IdIcon from '~/components/icons/IdIcon';
import PhoneIcon from '~/components/icons/PhoneIcon';
import { gqlQueryClient } from '~/lib/backend';
import { getHumanReadableFormSubmissionType } from '~/lib/files';
import meQuery from '~/queries/meQuery';
import { useNavigate, useParams } from '~/router';
import { kioskDealershipAtom } from '~/state/kiosk';
import { pdfUrlsToBlob } from './_utils';

const GloveCompartmentPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const kioskMode = useAtomValue(kioskDealershipAtom);

  const { transactionId, dealershipSlug } = useParams(
    '/dashboard/:dealershipSlug/:transactionId/glove-compartment'
  );
  const { data: userData, isLoading: loadingUserData } = useQuery(meQuery());
  const { data: formSubmissionsData, isLoading: loadingFormSubmissions } =
    useQuery({
      enabled: Boolean(userData?.me?.user?.id),
      queryKey: ['transaction', transactionId, 'customer', 'formSubmissions'],
      queryFn: async () =>
        gqlQueryClient({
          dealershipSlug
        })({
          customer: [
            {
              userId: userData?.me?.user?.id ?? 'no-user-id',
              transactionId
            },
            {
              formSubmissions: [
                {
                  filter: {
                    status: {
                      notIn: [FormSubmissionStatus.CANCELLED]
                    }
                  }
                },
                {
                  edges: {
                    node: {
                      formCollection: {
                        type: true,
                        status: true
                      },
                      id: true,
                      form: {
                        displayTitle: true
                      },
                      type: true,
                      file: {
                        url: true
                      }
                    }
                  }
                }
              ],
              documents: [
                {},
                {
                  edges: {
                    node: {
                      id: true,
                      title: true,
                      category: true,
                      pdfIsEncrypted: true,
                      file: {
                        url: true
                      }
                    }
                  }
                }
              ]
            }
          ]
        })
    });

  const formEdges = formSubmissionsData?.customer?.formSubmissions?.edges ?? [];
  const docEdges = formSubmissionsData?.customer?.documents?.edges ?? [];
  const formIds = formEdges.map((e) => e.node?.id ?? 'should-never-happen');
  const docIds = docEdges.map((e) => e.node?.id ?? 'should-never-happen');
  const allRowIds = [...formIds, ...docIds];

  const selectionIncludesEncryptedFile = docEdges.some((e) => {
    return (
      e.node?.id &&
      selectedIds.includes(e.node?.id) &&
      Boolean(e.node?.pdfIsEncrypted)
    );
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prevSelectedIds) => {
      const isSelected = prevSelectedIds.includes(id);

      // If it is already selected we remove it from the selected list
      if (isSelected) {
        return prevSelectedIds.filter((selectedId) => selectedId !== id);
      }

      // Otherwise add it
      return [...prevSelectedIds, id];
    });
  };

  const isAllSelected =
    allRowIds.length > 0 && selectedIds.length === allRowIds.length;

  const toggleSelectAll = () =>
    setSelectedIds(isAllSelected ? [] : [...allRowIds]);

  const handleOpenFormSubmission = (formSubmissionId: string) => {
    navigate(
      '/dashboard/:dealershipSlug/:transactionId/glove-compartment/:formSubmissionId',
      {
        params: { transactionId, formSubmissionId, dealershipSlug }
      }
    );
  };
  const handleOpenDocument = (documentId: string) => {
    navigate(
      '/dashboard/:dealershipSlug/:transactionId/glove-compartment/document/:documentId',
      {
        params: { transactionId, documentId, dealershipSlug }
      }
    );
  };

  const getFormSubmissionIcon = (roleEnum: string) => {
    switch (roleEnum) {
      case 'CONTACT_ACKNOWLEDGEMENT':
        return <PhoneIcon className="size-8" />;
      case 'IDENTITY_VERIFICATION':
        return <IdIcon className="size-8" />;
      default:
        return <DocumentIcon className="size-8" />;
    }
  };
  const getFormSubmissionIconColor = (roleEnum: string) => {
    switch (roleEnum) {
      case 'PRIVACY_POLICY':
        return '#333333';
      case 'CONTACT_ACKNOWLEDGEMENT':
        return '#46546B';
      case 'IDENTITY_VERIFICATION':
        return '#6297BA';
      default:
        return '#828282';
    }
  };

  const handleDownload = async () => {
    // REQ-KMSP-01
    if (kioskMode) {
      toast.error('Cannot download documents on Kiosk mode');
      return;
    }

    if (selectionIncludesEncryptedFile && selectedIds.length > 1) {
      toast.error(
        t('Cannot download multiple file when encrypted is selected')
      );
      return;
    }

    const formUrls = formEdges
      .filter(
        (e) =>
          selectedIds.includes(e.node?.id ?? 'should-never-happen') &&
          Boolean(e.node?.file?.url)
      )
      .map((e) => e.node?.file?.url ?? 'no-url');

    const docUrls = docEdges
      .filter(
        (e) =>
          selectedIds.includes(e.node?.id ?? 'should-never-happen') &&
          Boolean(e.node?.file?.url)
      )
      .map((e) => e.node?.file?.url ?? 'no-url');

    const documentUrls = [...formUrls, ...docUrls];

    try {
      const blob = await pdfUrlsToBlob(documentUrls);

      // Trigger download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'tdc-documents.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error(e);
      toast.error(
        t(
          'We could not download the documents. Please try again or contact support.'
        )
      );
    }
  };

  return (
    <div className="flex flex-col min-h-dvh">
      <Header
        title={t('View Documents')}
        leftElement={{
          type: 'CLOSE_AND_BACK_TO_TRANSACTION_MAIN_PAGE',
          transactionId,
          dealershipSlug
        }}
      />

      {/* 
        We only enable the download on Non Kiosk mode
        Enabling download on the Kiosk mode is a security risk (REQ-KMSP-01)
      */}

      {!kioskMode && (
        <div className="flex flex-col justify-between items-end px-8 pt-spacing-04">
          <div className="flex flex-row justify-between md:px-14 items-center w-full gap-spacing-02">
            <div
              className="flex flex-row justify-center items-center cursor-pointer space-x-spacing-04 h-full"
              onClick={toggleSelectAll}
            >
              <div
                className="flex flex-row items-center justify-center p-spacing-01 cursor-pointer "
                onClick={toggleSelectAll}
              >
                <Checkbox
                  checked={isAllSelected}
                  onChange={toggleSelectAll}
                  disabled={false}
                  size="LARGE"
                  labelClassName="flex flex-row items-center justify-center"
                />
              </div>

              <span className="heading-04">{t('Select All')}</span>
            </div>

            {selectionIncludesEncryptedFile && selectedIds.length > 1 ? (
              <div
                className={
                  'flex flex-row md:px-14  items-center justify-center'
                }
              >
                <Banner
                  message={
                    <p>{t('Encrypted files must downloaded individually')}</p>
                  }
                  type="ERROR"
                />
              </div>
            ) : (
              <div>
                <Button
                  onClick={handleDownload}
                  iconLeft={<Download />}
                  variant="PRIMARY"
                  disabled={
                    (selectionIncludesEncryptedFile &&
                      selectedIds.length > 1) ||
                    selectedIds.length === 0
                  }
                  label={`${t('Download')} (${selectedIds.length})`}
                  size="MEDIUM"
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col flex-1 overflow-y-auto">
        {(loadingUserData || loadingFormSubmissions) && (
          <div className="flex flex-1 items-center justify-center">
            <Spinner size="LARGE" color="GREY" />
          </div>
        )}

        {!loadingUserData && !loadingFormSubmissions && (
          <div className="flex flex-col overflow-y-auto px-8 py-spacing-04">
            <div className="flex flex-col space-y-0.5">
              {formSubmissionsData?.customer?.formSubmissions?.edges
                ?.filter((edge) => {
                  // This is a temporary fix to remove cancelled submissions from a form collection
                  return (
                    edge.node?.formCollection?.status !==
                    FormCollectionStatus.CANCELLED
                  );
                })
                .map((edge) => {
                  const formSubmission = edge.node;
                  return (
                    <SectionElementRow
                      key={formSubmission?.id}
                      title={
                        formSubmission?.form?.displayTitle ??
                        (formSubmission?.type
                          ? getHumanReadableFormSubmissionType(
                              formSubmission.type,
                              t
                            )
                          : t('No form title'))
                      }
                      onClick={() => {
                        handleOpenFormSubmission(
                          formSubmission?.id ?? 'no-form-submission-id'
                        );
                      }}
                      iconLeft={
                        <div className="relative">
                          {getFormSubmissionIcon(
                            formSubmission?.type ?? 'no-form-submission-type'
                          )}
                        </div>
                      }
                      iconLeftColor={getFormSubmissionIconColor(
                        formSubmission?.type ?? 'no-form-submission-type'
                      )}
                      iconRight={
                        <div className="relative">
                          <ChevronRightIcon className="w-8 aspect-square  " />
                        </div>
                      }
                      checked={selectedIds.includes(
                        formSubmission?.id ?? 'no-document-id'
                      )}
                      onCheckboxChange={() => {
                        toggleSelect(formSubmission?.id ?? 'no-document-id');
                        console.log(selectedIds);
                      }}
                    />
                  );
                })}
              {formSubmissionsData?.customer?.documents?.edges?.map(
                ({ node: document }) => {
                  return (
                    <SectionElementRow
                      key={document?.id}
                      title={document?.title ?? t('No form title')}
                      onClick={() => {
                        handleOpenDocument(document?.id ?? 'no-document-id');
                      }}
                      iconLeft={
                        <div className="relative">
                          {getFormSubmissionIcon(
                            document?.category ?? 'no-document-categoyr'
                          )}
                        </div>
                      }
                      iconLeftColor={getFormSubmissionIconColor(
                        document?.category ?? 'no-document-category'
                      )}
                      iconRight={
                        <div className="relative">
                          <ChevronRightIcon className="w-8 aspect-square  " />
                        </div>
                      }
                      checked={selectedIds.includes(
                        document?.id ?? 'no-document-id'
                      )}
                      onCheckboxChange={() =>
                        toggleSelect(document?.id ?? 'no-document-id')
                      }
                      isEncrypted={document?.pdfIsEncrypted ?? false}
                    />
                  );
                }
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GloveCompartmentPage;
